/**
 * api — axios instance pre-wired for the Express server.
 *
 * Base URL is relative (the React build is served by the same Express
 * process at the same origin, so /api/* just works). For local dev with
 * the CRA dev server on a different port, set REACT_APP_API_BASE_URL in
 * .env.local.
 *
 * Auth (proxied — the browser never talks to Supabase directly):
 *   - Requests get an Authorization: Bearer header from the locally stored
 *     captain session (see lib/session.js).
 *   - On a 401 from a protected route, we transparently call
 *     POST /api/auth/refresh once, store the new tokens, and retry. If the
 *     refresh fails, we clear the session and fire AUTH_EXPIRED_EVENT so the
 *     app can redirect to the login page.
 *   - Login itself goes through loginRequest() below.
 */

import axios from 'axios';

import {
  AUTH_EXPIRED_EVENT,
  clearSession,
  getAccessToken,
  getRefreshToken,
  getSession,
  setSession,
} from './session';

const baseURL = process.env.REACT_APP_API_BASE_URL || '';

const axiosConfig = {
  baseURL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
};

export const api = axios.create(axiosConfig);

// A bare client with NO interceptors, used for the auth endpoints themselves
// (login + refresh) so they can never trigger the refresh-retry loop.
const authHttp = axios.create(axiosConfig);

// Attach the captain's access token to every request that has one.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Shared in-flight refresh promise so a burst of 401s triggers ONE refresh.
let refreshing = null;

function refreshSession() {
  if (!refreshing) {
    const refreshToken = getRefreshToken();
    refreshing = authHttp
      .post('/api/auth/refresh', { refresh_token: refreshToken })
      .then((res) => {
        // Merge so we keep `user` even if refresh omits it.
        const next = { ...(getSession() || {}), ...res.data };
        setSession(next);
        return next;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthRoute = (original?.url || '').includes('/api/auth/');

    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isAuthRoute &&
      getRefreshToken()
    ) {
      original._retried = true;
      try {
        const next = await refreshSession();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${next.access_token}`;
        return api(original);
      } catch (refreshErr) {
        clearSession();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * loginRequest — POST credentials to the server's proxied login endpoint.
 * Uses the bare client so no stale token / refresh logic interferes. Throws
 * an Error carrying the server's message (and .status) on failure.
 */
export async function loginRequest(email, password) {
  try {
    const { data } = await authHttp.post('/api/auth/login', { email, password });
    return data;
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      'Sign in failed. Check your email and password.';
    const wrapped = new Error(message);
    wrapped.status = err?.response?.status;
    throw wrapped;
  }
}

export default api;
